import { useCallback, useMemo } from 'react';
import { AutocompleteInputChangeReason } from '@mui/material';
import { AutocompleteOption } from '@/common/presentation/hooks/useAutocomplete';
import { Dtos_V1_SpecialFlagDto } from '@/common/infrastructure/gql/generated/schema';

interface UseSpecialFlagAutocompleteProps {
  availableSpecialFlags?: Dtos_V1_SpecialFlagDto[];
  loading?: boolean;
  rawSpecialFlag?: Dtos_V1_SpecialFlagDto | null;
  selectedSpecialFlagId?: number | null;
  onRemove?: () => void;
}

interface UseSpecialFlagAutocompleteReturn {
  specialFlagOptions: AutocompleteOption[];
  specialFlagFilterOptions: (options: AutocompleteOption[], state: { inputValue: string }) => AutocompleteOption[];
  handleSpecialFlagInputChange: (_: unknown, _value: string, _reason?: AutocompleteInputChangeReason) => void;
  initialSpecialFlagOption?: AutocompleteOption;
  selectedSpecialFlag: Dtos_V1_SpecialFlagDto | null;
  handleRemoveSpecialFlag: () => void;
  specialFlagMap: Map<number, Dtos_V1_SpecialFlagDto>;
  loading: boolean;
}

export const useSpecialFlagAutocomplete = ({
  availableSpecialFlags = [],
  loading = false,
  rawSpecialFlag,
  selectedSpecialFlagId,
  onRemove
}: UseSpecialFlagAutocompleteProps): UseSpecialFlagAutocompleteReturn => {
  const specialFlagOptions = useMemo(() => {
    if (!availableSpecialFlags || availableSpecialFlags.length === 0) return [];
    return availableSpecialFlags.map((flag) => ({
      value: flag.id,
      label: flag.name ?? 'Unknown Flag'
    }));
  }, [availableSpecialFlags]);

  const specialFlagMap = useMemo(() => {
    if (!availableSpecialFlags || availableSpecialFlags.length === 0) return new Map<number, Dtos_V1_SpecialFlagDto>();
    return new Map(availableSpecialFlags.map((flag) => [flag.id, flag]));
  }, [availableSpecialFlags]);

  const specialFlagFilterOptions = useCallback((options: AutocompleteOption[], state: { inputValue: string }) => {
    if (!state.inputValue) return options;
    const searchLower = state.inputValue.toLowerCase();
    return options.filter((option) => option.label?.toLowerCase().includes(searchLower) ?? false);
  }, []);

  const handleSpecialFlagInputChange = useCallback((_: unknown, _value: string, _reason?: AutocompleteInputChangeReason) => {
    // Filtering is handled by filterOptions prop, no need to track search term
  }, []);

  const initialSpecialFlagOption = useMemo(() => {
    if (rawSpecialFlag) {
      return {
        value: rawSpecialFlag.id ?? 0,
        label: rawSpecialFlag.name ?? 'Unknown Flag'
      };
    }
    return undefined;
  }, [rawSpecialFlag]);

  const selectedSpecialFlag = useMemo(() => {
    if (!selectedSpecialFlagId || specialFlagMap.size === 0) return null;
    return specialFlagMap.get(selectedSpecialFlagId) ?? null;
  }, [selectedSpecialFlagId, specialFlagMap]);

  const handleRemoveSpecialFlag = useCallback(() => {
    onRemove?.();
  }, [onRemove]);

  return {
    specialFlagOptions,
    specialFlagFilterOptions,
    handleSpecialFlagInputChange,
    initialSpecialFlagOption,
    selectedSpecialFlag,
    handleRemoveSpecialFlag,
    specialFlagMap,
    loading
  };
};
